import { useFormikContext } from 'formik';

import Dropdown from '@/components/common/Dropdown/Dropdown';
import { NumberUtils } from '@/utils/numbers.utils';
import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import { UpliftModifierEnum, UpliftCapRateTypeEnum } from '../upliftCaps.types';
import { IndexOption } from '../hooks/useGetUpliftIndexesForCaps';
import { UpliftCapsFormValues } from './UpliftCapsForm';
import styles from './uplift-caps-form.module.scss';

interface ModifierOption {
  label: string;
  value: UpliftModifierEnum;
}

interface RateTypeOption {
  label: string;
  value: UpliftCapRateTypeEnum;
}

const modifier1Options: ModifierOption[] = [
  { label: 'None', value: UpliftModifierEnum.None },
  { label: 'Equals', value: UpliftModifierEnum.Equals },
  { label: 'Greater Than', value: UpliftModifierEnum.GreaterThan },
  { label: 'Less Than', value: UpliftModifierEnum.LessThan },
  { label: 'Sum Of', value: UpliftModifierEnum.SumOf },
  { label: 'Maximum Of', value: UpliftModifierEnum.MaximumOf },
];

const modifier2Options: ModifierOption[] = [
  { label: 'N/A', value: UpliftModifierEnum.None },
  { label: 'A Maximum Of', value: UpliftModifierEnum.MaximumOf },
];

const rateTypeOptions: RateTypeOption[] = [
  { label: 'Index', value: UpliftCapRateTypeEnum.Index },
  { label: 'Fixed', value: UpliftCapRateTypeEnum.Fixed },
];

interface IUpliftCapSectionProps {
  prefix: 'existingUpliftCap' | 'furtherUpliftCap';
  isEndDateRequired: boolean;
  isDisabled: boolean;
  isEditable: boolean;
  indexOptions: IndexOption[];
  isLoadingIndexes: boolean;
}

export default function UpliftCapSection({
  prefix,
  isEndDateRequired,
  isDisabled,
  indexOptions,
  isLoadingIndexes,
}: IUpliftCapSectionProps) {
  const { values, setFieldValue, errors, touched } = useFormikContext<UpliftCapsFormValues>();

  const capValues = values[prefix];
  if (!capValues) return null;

  const capErrors = (errors[prefix] as Record<string, string> | undefined) ?? {};
  const capTouched = (touched[prefix] as Record<string, boolean> | undefined) ?? {};

  const isNone = capValues.modifier1 === UpliftModifierEnum.None;
  const showRate2 = !isNone
    && capValues.modifier1 !== UpliftModifierEnum.Equals;
  const showModifier2 = showRate2;
  const showRate3 = capValues.modifier2 === UpliftModifierEnum.MaximumOf;

  const setCapField = (field: string, value: unknown) => {
    setFieldValue(`${prefix}.${field}`, value);
  };

  const renderRateField = (
    ratePrefix: 'rate1' | 'rate2' | 'rate3',
    label: string
  ) => {
    const typeField = `${ratePrefix}Type` as const;
    const indexIdField = `${ratePrefix}IndexId` as const;
    const fixedValueField = `${ratePrefix}FixedValue` as const;

    const rateType = capValues[typeField] as UpliftCapRateTypeEnum;
    const indexId = capValues[indexIdField] as string | null;
    const fixedValue = capValues[fixedValueField] as number | null;

    const indexError = capErrors[indexIdField];
    const indexTouched = capTouched[indexIdField];
    const fixedError = capErrors[fixedValueField];
    const fixedTouched = capTouched[fixedValueField];

    return (
      <div className={styles.fieldRow}>
        <label className={styles.fieldLabel}>{label}</label>
        <div className={styles.rateFieldGroup}>
          <div className={styles.rateTypeDropdown}>
            <Dropdown<RateTypeOption>
              optionsData={rateTypeOptions}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              selectedOption={
                rateTypeOptions.find((opt) => opt.value === rateType) ?? null
              }
              handleChange={(opt) => {
                setCapField(typeField, opt?.value ?? UpliftCapRateTypeEnum.Index);
                if (opt?.value === UpliftCapRateTypeEnum.Index) {
                  setCapField(fixedValueField, null);
                } else {
                  setCapField(indexIdField, null);
                }
              }}
              disabled={isDisabled}
              name={`${prefix}.${typeField}`}
            />
          </div>
          <div className={styles.rateValueField}>
            {rateType === UpliftCapRateTypeEnum.Index ? (
              <>
                <Dropdown<IndexOption>
                  optionsData={indexOptions}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.id}
                  selectedOption={
                    indexOptions.find((opt) => opt.id === indexId) ?? null
                  }
                  handleChange={(opt) => {
                    setCapField(indexIdField, opt?.id ?? null);
                  }}
                  disabled={isDisabled}
                  isLoading={isLoadingIndexes}
                  isSearchable={true}
                  isClearable={true}
                  name={`${prefix}.${indexIdField}`}
                  placeholder="Select index..."
                />
                {indexError && indexTouched && (
                  <span className={styles.fieldError}>{indexError}</span>
                )}
              </>
            ) : (
              <>
                <input
                  type="number"
                  step="0.01"
                  className={`${styles.fixedInput} ${
                    fixedError && fixedTouched ? styles.fixedInputError : ''
                  }`}
                  value={fixedValue ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : NumberUtils.parseNumericInput(e.target.value);
                    setCapField(fixedValueField, val);
                  }}
                  disabled={isDisabled}
                  placeholder="0.00"
                />
                {fixedError && fixedTouched && (
                  <span className={styles.fieldError}>{fixedError}</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.capSection}>
      {/* Modifier */}
      <div className={styles.fieldRow}>
        <label className={styles.fieldLabel}>Modifier</label>
        <Dropdown<ModifierOption>
          optionsData={modifier1Options}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
          selectedOption={
            modifier1Options.find((opt) => opt.value === capValues.modifier1) ?? null
          }
          handleChange={(opt) => {
            const newVal = opt?.value ?? UpliftModifierEnum.None;
            setCapField('modifier1', newVal);
            if (newVal === UpliftModifierEnum.None || newVal === UpliftModifierEnum.Equals) {
              setCapField('rate2Type', UpliftCapRateTypeEnum.Index);
              setCapField('rate2IndexId', null);
              setCapField('rate2FixedValue', null);
              setCapField('modifier2', UpliftModifierEnum.None);
              setCapField('rate3Type', UpliftCapRateTypeEnum.Index);
              setCapField('rate3IndexId', null);
              setCapField('rate3FixedValue', null);
            }
          }}
          disabled={isDisabled}
          name={`${prefix}.modifier1`}
        />
        {capErrors.modifier1 && capTouched.modifier1 && (
          <span className={styles.fieldError}>{capErrors.modifier1}</span>
        )}
      </div>

      {/* End Date — hidden when modifier1 is None */}
      {!isNone && (
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            End Date{isEndDateRequired ? ' *' : ''}
          </label>
          <DatePickerTag4
            value={capValues.endDate}
            onChange={(event) => {
              setCapField('endDate', event.value ?? null);
            }}
            disabled={isDisabled}
            clearButton={true}
            name={`${prefix}.endDate`}
          />
          {capErrors.endDate && capTouched.endDate && (
            <span className={styles.fieldError}>{capErrors.endDate}</span>
          )}
        </div>
      )}

      {/* Rate 1 — hidden when modifier1 is None */}
      {!isNone && renderRateField('rate1', 'Rate 1')}

      {/* Rate 2 (shown when modifier1 is GreaterThan/LessThan/SumOf/MaximumOf) */}
      {showRate2 && renderRateField('rate2', 'Rate 2')}

      {/* Modifier 2 (shown when modifier1 is GreaterThan/LessThan/SumOf/MaximumOf) */}
      {showModifier2 && (
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>Modifier 2</label>
          <Dropdown<ModifierOption>
            optionsData={modifier2Options}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
            selectedOption={
              modifier2Options.find((opt) => opt.value === capValues.modifier2) ?? null
            }
            handleChange={(opt) => {
              const newVal = opt?.value ?? UpliftModifierEnum.None;
              setCapField('modifier2', newVal);
              if (newVal === UpliftModifierEnum.None) {
                setCapField('rate3Type', UpliftCapRateTypeEnum.Index);
                setCapField('rate3IndexId', null);
                setCapField('rate3FixedValue', null);
              }
            }}
            disabled={isDisabled}
            name={`${prefix}.modifier2`}
          />
        </div>
      )}

      {/* Rate 3 (shown when modifier2 is MaximumOf) */}
      {showRate3 && renderRateField('rate3', 'Rate 3')}

      {/* Calculated Cap Percentage — readonly, API-computed */}
      {!isNone && (
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            Calculated Cap Percentage
          </label>
          <input
            type="text"
            className={styles.fixedInput}
            value={
              capValues.calculatedCapPercentage != null
                ? `${capValues.calculatedCapPercentage}%`
                : '—'
            }
            readOnly
            disabled
          />
        </div>
      )}
    </div>
  );
}
